import { getQueryParam } from '@crehana/utils';

import type { GetInitialStateInner } from 'Jsx/Utils/getBaseInitialState';

import { reduceById } from './utils';
import type { RawPageData, AppProps, CategoryFilter, Filters } from './types';

const getLanguageItems = (languageKey: string) => {
  const spanishLanguageItem = {
    id: 1,
    slug: 'es',
    name: languageKey === 'pt' ? 'Espanhol' : 'Español',
  };

  const portugueseLanguageItem = {
    id: 2,
    slug: 'pt',
    name: languageKey === 'pt' ? 'Português' : 'Portugués',
  };

  return {
    spanishLanguageItem,
    portugueseLanguageItem,
  };
};

type languageItemType = {
  id: number;
  slug: string;
  name: string;
};

const getLanguageByQueryParam = (
  languageParams: string[],
  spanishLanguageItem: languageItemType,
  portugueseLanguageItem: languageItemType,
): any => {
  const selectedLanguages: Record<string, languageItemType> = {};

  languageParams.forEach(selectedLang => {
    if (selectedLang === 'pt') {
      selectedLanguages[portugueseLanguageItem.id] = portugueseLanguageItem;
    } else if (selectedLang === 'es') {
      selectedLanguages[spanishLanguageItem.id] = spanishLanguageItem;
    }
  });

  return selectedLanguages;
};

const getInitialSelectedLanguage = (languageKey: string) => {
  const languageParams = getQueryParam('idiomas');
  const { spanishLanguageItem, portugueseLanguageItem } =
    getLanguageItems(languageKey);

  if (
    languageParams &&
    typeof languageParams === 'string' &&
    languageParams.split(',').length > 0
  ) {
    return getLanguageByQueryParam(
      languageParams.split(','),
      spanishLanguageItem,
      portugueseLanguageItem,
    );
  }

  return {};
};

const getInitialSelectedDuration = (): string[] => {
  const durationParams = getQueryParam('duraciones');

  if (
    durationParams &&
    typeof durationParams === 'string' &&
    durationParams.split(',').length > 0
  ) {
    return durationParams.split(',');
  }

  return [];
};

const getInitialState: GetInitialStateInner<AppProps, RawPageData> = (
  data,
  _user,
  {
    LANGUAGE_KEY,
    keys_crehana_urls: keysCrehanaUrl,
    feature_flags: featureFlags,
  },
) => {
  // =============================== initialize filters ========================= //

  const categorySelectId = data.filters.category
    ? `${data.filters.category.order}`
    : null;
  const subCategorySelectedId = data.filters.sub_category
    ? data.filters.sub_category.id
    : null;
  const levelsSelected = data.filters.levels.reduce(reduceById, {});
  const softwaresSelected = data.filters.softwares.reduce(reduceById, {});

  const allCourses = data.show_all || getQueryParam('all');
  const showRecommendedForMeCourses =
    data.recommeded_for_me || getQueryParam('recommended-for-me');

  const softwares = {
    id: 'software-filter',
    open: Boolean(Object.keys(softwaresSelected).length),
    name: 'CATALOG_SOFTWARE',
    subItems: keysCrehanaUrl.softwares.reduce(reduceById, {}),
  };
  const levels = {
    id: 'level-filter',
    open: Boolean(Object.keys(levelsSelected).length),
    name: 'CATALOG_NIVEL',
    subItems: data.levels.reduce(reduceById, {}),
  };

  const { spanishLanguageItem, portugueseLanguageItem } =
    getLanguageItems(LANGUAGE_KEY);

  const languages = {
    id: 'language-filter',
    open: false,
    name: 'LANGUAGE',
    subItems: [spanishLanguageItem, portugueseLanguageItem].reduce(
      reduceById,
      {},
    ),
  };

  const selectedLanguages = getInitialSelectedLanguage(LANGUAGE_KEY);
  const durationsSelected = getInitialSelectedDuration();

  const filters: Filters = {
    allCourses: !!allCourses,
    showRecommendedForMeCourses: !!showRecommendedForMeCourses,
    categorySelected: {
      itemId: categorySelectId,
      slug: data.filters.category.slug,
      subItemId: subCategorySelectedId || (categorySelectId ? 0 : null),
    },
    categoryOpen: {
      itemId: categorySelectId,
    },
    levelsSelected,
    softwaresSelected,
    categories: keysCrehanaUrl.categories
      .map(c => ({
        ...c,
        subItems: {
          0: {
            id: 0,
            name: 'Todos los cursos',
            slug: c.slug,
          },
          ...c.subcategories.reduce(reduceById, {}),
        },
      }))
      .reduce((obj, item) => {
        // created to order categories by keys
        const fakeId = `${item.order}`;

        obj[fakeId] = {
          ...item,
          // originalId: item.id,
          id: fakeId,
          open: categorySelectId === fakeId,
        };

        return obj;
      }, {} as Record<string, CategoryFilter>),
    levels,
    languages,
    defaultLanguages: languages,
    languagesArray: [spanishLanguageItem, portugueseLanguageItem],
    selectedLanguages,
    softwares,
    defaultSoftwares: softwares,
    defaultLevels: levels,
    mobileSelectionLanguages: {
      selected:
        Object.values(selectedLanguages || {}).map((l: any) => l.id) || [],
      edited: [],
      dirty: false,
    },
    mobileSelectionLevels: {
      selected: Object.values(levelsSelected || {}).map((l: any) => l.id) || [],
      edited: [],
      dirty: false,
    },
    mobileSelectionSoftwares: {
      selected:
        Object.values(softwaresSelected || {}).map((l: any) => l.id) || [],
      edited: [],
      dirty: false,
    },
    mobileSelectionDuration: {
      selected: durationsSelected.slice(),
      edited: [],
      dirty: false,
    },
    // @ts-expect-error
    openDialog: false,
    duration: {
      selectedTags: durationsSelected,
    },
  };

  const orderByItems = data.order_by.map(ob => ({
    ...ob,
    label: ob.name,
    value: ob.slug,
  }));

  const defaultOrderBy = orderByItems.find(o => o.slug === 'online')!;
  const orderBySelected = orderByItems.find(
    o => o.slug === data.filters.order_by.slug,
  )!;

  const featureFlagStartMarathonPopup = featureFlags
    ? featureFlags.find(f => f.key === 'enable_marathon_start_popup')
    : undefined;

  return {
    filters,
    orderBy: {
      itemSelected: {
        ...orderBySelected,
        label: orderBySelected.name,
        value: orderBySelected.slug,
      },
      defaultOrderBy,
      items: orderByItems,
    },
    tracks: data.tracks?.items.edges.map(el => el.node) || [],
    banners: data.banners,
    categoriesBanners: data.categories_banners,
    // pathname ex: /pe/cursos-gratis
    // Only works in SSR, set client pathname in extraState
    pathname: data.pathname,
    initialSeoLongDescription: data?.seo?.description,
    featureFlagStartMarathonPopup: featureFlagStartMarathonPopup?.is_active,
    campaignSlug: data.page_catalog.slug,
  };
};

export default getInitialState;
